import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard, GitBranch, Megaphone, CreditCard, UserCog,
  MessageSquare, ExternalLink, Loader2, FileText, ListOrdered, UserCircle, Workflow,
  Users, BarChart3, Activity, ScrollText
} from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

const NAV_ITEMS = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Analytics', icon: BarChart3, path: '/dashboard/analytics' },
  { label: 'Flows', icon: GitBranch, path: '/dashboard/flows' },
  { label: 'Flow Builder', icon: Workflow, path: '/dashboard/flow-builder' },
  { label: 'Templates', icon: FileText, path: '/dashboard/templates' },
  { label: 'Sequences', icon: ListOrdered, path: '/dashboard/sequences' },
  { label: 'Campaigns', icon: Megaphone, path: '/dashboard/campaigns' },
  { label: 'Audience Segments', icon: Users, path: '/dashboard/segments' },
  { label: 'Smart Handoff', icon: UserCircle, path: '/dashboard/routing' },
  { label: 'Queue Monitor', icon: Activity, path: '/dashboard/queue' },
  { label: 'WhatsApp Logs', icon: ScrollText, path: '/dashboard/whatsapp-logs' },
  { label: 'Marketing', icon: Megaphone, path: '/dashboard/marketing' },
  { label: 'Billing', icon: CreditCard, path: '/dashboard/billing' },
  { label: 'Profile / Settings', icon: UserCog, path: '/dashboard/profile' },
];

function NavContent({ onLinkClick }) {
  const location = useLocation();
  const { getChatwootSSO } = useAuth();
  const [loading, setLoading] = useState(false);

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const handleChatwoot = async () => {
    setLoading(true);
    try {
      const result = await getChatwootSSO();
      if (result?.success && result?.sso_url) {
        window.open(result.sso_url, '_blank');
      }
    } catch (e) {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="h-16 flex items-center px-5 border-b border-gray-100 shrink-0">
        <Link to="/" className="flex items-center gap-2.5" data-testid="sidebar-logo">
          <img src="/giri-logo.png" alt="GiriChat" className="w-7 h-7 object-contain rounded-md" />
          <span className="text-[15px] font-semibold text-slate-900">GiriChat</span>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-5 px-3 space-y-0.5 overflow-y-auto">
        <p className="px-3 pb-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
          Main Menu
        </p>
        {NAV_ITEMS.map(({ label, icon: Icon, path }) => {
          const active = isActive(path);
          return (
            <Link
              key={path}
              to={path}
              onClick={onLinkClick}
              data-testid={`nav-${path.replace('/dashboard', '').replace('/', '') || 'overview'}`}
              className={`flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-150 ${
                active
                  ? 'bg-[#00D1B2]/10 text-[#00D1B2] pl-[11px] py-2.5 pr-3 border-l-[3px] border-[#00D1B2]'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 px-3 py-2.5'
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </Link>
          );
        })}
      </nav>

      {/* Switch to Chat */}
      <div className="p-4 border-t border-gray-100 shrink-0">
        <Button
          onClick={() => { onLinkClick(); handleChatwoot(); }}
          disabled={loading}
          data-testid="switch-to-chat-btn"
          className="w-full bg-[#00D95F] hover:bg-[#00C054] text-white gap-2 text-sm font-semibold h-10"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <MessageSquare className="w-4 h-4" />
              Switch to Chat
              <ExternalLink className="w-3 h-3 opacity-70" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export function Sidebar({ open, onClose }) {
  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-100 shrink-0 h-screen sticky top-0"
        data-testid="dashboard-sidebar"
      >
        <NavContent onLinkClick={() => {}} />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent side="left" className="p-0 w-64 bg-white">
          <NavContent onLinkClick={onClose} />
        </SheetContent>
      </Sheet>
    </>
  );
}
